/* eslint-disable import/order */
import React from 'react'
import { useSelector } from 'react-redux'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HistoryRouter } from 'redux-first-history/rr6'

import { Toaster } from '@/components/ui/sonner'

import { Error404 } from './components/ErrorState/Error404'
import { usePageviewTracking } from './hooks/usePageviewTracking'
import useTheme from './hooks/useSwitchTheme'
import { initializeSentry } from './initializeSentry'
import { ContactUs } from './pages/ContactUs/ContactUs'
import { Home } from './pages/Home/Home'
import { LeftSideBar } from './pages/LeftSideBar/LeftSideBar'
import { AuthSuccess } from './pages/Auth'
import { SetPassword } from './pages/Auth/SetPassword'
import { CreatePasswordForm } from './pages/Login/CreatePasswordForm'
import { ForgotPasswordForm } from './pages/Login/ForgotPasswordForm'
import { ForgotPaswordCheckYourInbox } from './pages/Login/ForgotPaswordCheckYourInbox'
import { Login } from './pages/Login/Login'
import { LoginError } from './pages/Login/LoginError'
import { LoginForm } from './pages/Login/LoginForm'

import { ReportsScreen } from './pages/Reports/ReportsScreen'
import { DigitalFootprintForm } from './pages/DigitalFootprintForm/DigitalFootprintForm'
import { Settings } from './pages/Settings/Settings'
import { EmailVerification } from './pages/SignUp/EmailVerification'
import { SignUp } from './pages/SignUp/SignUp'
import { SignUpStart } from './pages/SignUp/SignUpStart'
import { selectInMaintenance } from './store/api/Config/Config.selector'
import { history } from './store/store'
import './i18n/config'
import { GeneratingReport } from './pages/GeneratingReport/GeneratingReport'

import { BillingAndInvoices } from './pages/BillingAndInvoices/BillingAndInvoices'
import { DigitalReport } from './pages/DigitalReport/DigitalReport'
import { SocialMediaProfile } from './pages/DigitalReport/SocialMediaProfile'
import { KYCVerification } from './pages/KYC/KYCVerification'
import { KYCVerificationInProgress } from './pages/KYC/KYCVerificationInProgress'
import { WelcomeToSelfCheck } from './pages/SignUp/WelcomeToSelfCheck'
import { Motivation } from './pages/SignUp/Motivation'
import { SetupDigitalFootprint } from './pages/Setup/SetupDigitalFootprint'
import { DefaultBehaviorAndTopics } from './pages/Setup/DefaultBehavior&Topics'
import { GeneratingReportMock } from './pages/Payment/GeneratingReportMock'
import { PaymentPaywall } from './pages/Payment/PaymentPaywall'
import { KYCVerificationSuccess } from './pages/KYC/KYCVerificationSuccess'
import { KYCVerificationFailed } from './pages/KYC/KYCVerificationFailed'
import { PaymentSuccess } from './pages/Payment/PaymentSuccess'
import { PaymentRedirect } from './pages/Payment/PaymentRedirect'
import { Maintenance } from './pages/Maintenance/Maintenance'
import { SignUpContactUs } from './pages/SignUp/SignUpContactUs'
import { SettingsVerifyPhoneNumber } from './pages/Settings/components/VerifyPhoneNumber/SettingsVerifyPhoneNumber'
import { SettingsPhoneVerification } from './pages/Settings/components/VerifyPhoneNumber/SettingsPhoneVerification'
import { SetPersonalInfo } from './pages/SignUp/SetPersonalInfo'
import { RevealDigitalFootprint } from './pages/PublicForm/RevealDigitalFootprint'
import { ProspectEmailVerification } from './pages/PublicForm/ProspectEmailVerification'
import { PublicPaymentPaywall } from './pages/PublicForm/PublicPaymentPaywall'
import { ImpersonationConfirmInfo } from './pages/Impersonation/ImpersonationConfirmInfo'
import { ImpersonationScreening } from './pages/Impersonation/ImpersonationScreening'
import { ImpersonationPaywall } from './pages/Impersonation/ImpersonationPaywall'
import { ImpersonationPaymentSuccess } from './pages/Impersonation/ImpersonationPaymentSuccess'
import { ImpersonationGeneratingReport } from './pages/Impersonation/ImpersonationGeneratingReport'
import { ImpersonationReport } from './pages/Impersonation/ImpersonationReport'

initializeSentry()

/**
 * Inner component that handles routing and pageview tracking
 * Must be inside HistoryRouter for useLocation to work
 */
const AppRoutes: React.FC = () => {
  usePageviewTracking()
  const showMaintenanceScreen = useSelector(selectInMaintenance)

  if (showMaintenanceScreen) {
    return <Maintenance />
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" />} />

      {/* Auth Success Route - outside of Login layout */}
      <Route path="auth/success" element={<AuthSuccess />} />

      {/* Stripe Payment Redirect - outside of Login layout */}
      <Route path="stripe/payment-redirect" element={<PaymentRedirect />} />

      {/* Login */}
      <Route element={<Login />}>
        <Route path="login" element={<LoginForm />} />
        <Route path="forgot-password" element={<ForgotPasswordForm />} />
        <Route path="create-password" element={<CreatePasswordForm />} />
        <Route
          path="forgot-pasword-check-your-inbox"
          element={<ForgotPaswordCheckYourInbox />}
        />
        <Route path="error" element={<LoginError />} />
      </Route>

      {/* Sign Up */}
      <Route path="sign-up" element={<SignUp />}>
        <Route path="get-started" element={<SignUpStart />} />
        <Route path="OTP-email" element={<EmailVerification />} />
        <Route path="welcome" element={<WelcomeToSelfCheck />} />
        <Route path="motivation" element={<Motivation />} />
        <Route path="contact-us" element={<SignUpContactUs />} />
        <Route path="set-password" element={<SetPassword />} />
        <Route path="personal-info" element={<SetPersonalInfo />} />
      </Route>

      {/* Public Form */}
      <Route path="reveal-digital-footprint" element={<SignUp />}>
        <Route index element={<RevealDigitalFootprint />} />
      </Route>
      <Route path="plans" element={<SignUp />}>
        <Route index element={<PublicPaymentPaywall />} />
      </Route>
      <Route path="verification" element={<SignUp />}>
        <Route index element={<ProspectEmailVerification />} />
      </Route>

      {/* Setup */}
      <Route path="setup" element={<SignUp />}>
        <Route path="digital-footprint" element={<SetupDigitalFootprint />} />
        <Route path="flags" element={<DefaultBehaviorAndTopics />} />
      </Route>

      {/* KYC */}
      <Route path="kyc" element={<SignUp />}>
        <Route path="start-verification" element={<KYCVerification />} />
        <Route
          path="verification-in-progress"
          element={<KYCVerificationInProgress />}
        />
        <Route
          path="verification-success"
          element={<KYCVerificationSuccess />}
        />
        <Route path="verification-failed" element={<KYCVerificationFailed />} />
      </Route>

      {/* Logged Layout */}
      <Route element={<LeftSideBar />}>
        {/* Payment */}
        <Route>
          <Route path="preparing-report" element={<GeneratingReportMock />} />
          <Route path="paywall" element={<PaymentPaywall />} />
          <Route path="payment" element={<PaymentPaywall />} />
          <Route path="payment-success" element={<PaymentSuccess />} />
        </Route>

        {/* Dashboard */}
        <Route path="dashboard">
          {/* Main Sidebar routes */}
          <Route path="home" index element={<Home />} />
          <Route path="contact-us" element={<ContactUs />} />
          <Route path="settings" element={<Settings />} />
          <Route
            path="settings/verify-phone"
            element={<SettingsVerifyPhoneNumber />}
          />
          <Route
            path="settings/verify-phone/otp"
            element={<SettingsPhoneVerification />}
          />
          <Route path="reports" element={<ReportsScreen />} />
          <Route path="billing-and-invoices" element={<BillingAndInvoices />} />
        </Route>

        {/* Report routes */}
        <Route path="report">
          <Route path="social-media-profile" element={<SocialMediaProfile />} />
          <Route path="digital-report" element={<DigitalReport />} />
        </Route>

        {/* Search */}
        <Route path="search">
          <Route path="confirm-info" element={<DigitalFootprintForm />} />
          <Route path="edit-profile" element={<DigitalFootprintForm />} />
          <Route path="generating-report" element={<GeneratingReport />} />
        </Route>

        {/* Impersonation Check */}
        <Route path="impersonation">
          <Route path="confirm-info" element={<ImpersonationConfirmInfo />} />
          <Route path="screening" element={<ImpersonationScreening />} />
          <Route path="paywall" element={<ImpersonationPaywall />} />
          <Route
            path="payment-success"
            element={<ImpersonationPaymentSuccess />}
          />
          <Route
            path="generating-report"
            element={<ImpersonationGeneratingReport />}
          />
          <Route path="report" element={<ImpersonationReport />} />
        </Route>
      </Route>

      <Route path="*" element={<Error404 />} />
    </Routes>
  )
}

const App: React.FC = () => {
  useTheme()

  return (
    <>
      <HistoryRouter history={history}>
        <AppRoutes />
      </HistoryRouter>

      <Toaster richColors />
    </>
  )
}

export default App
